import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const MethodologySection = () => {
  const [activeFramework, setActiveFramework] = useState(0);

  const handleDownloadGuide = () => {
    const doc = new jsPDF();
    const pageWidth = doc?.internal?.pageSize?.getWidth();
    const pageHeight = doc?.internal?.pageSize?.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Brand colors
    const primaryColor = [37, 99, 235]; // Blue
    const secondaryColor = [59, 130, 246]; // Light blue
    const accentColor = [16, 185, 129]; // Green
    const darkGray = [55, 65, 81];
    const lightGray = [156, 163, 175];

    // Helper function to add new page if needed
    const checkPageBreak = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc?.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Cover Page
    doc?.setFillColor(...primaryColor);
    doc?.rect(0, 0, pageWidth, pageHeight, 'F');
    
    // Logo placeholder (white circle)
    doc?.setFillColor(255, 255, 255);
    doc?.circle(pageWidth / 2, 60, 20, 'F');
    
    // Title
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(32);
    doc?.setFont('helvetica', 'bold');
    doc?.text('NORTHSTAR ANALYTICS', pageWidth / 2, 110, { align: 'center' });
    
    doc?.setFontSize(24);
    doc?.setFont('helvetica', 'normal');
    doc?.text('Methodology Guide', pageWidth / 2, 130, { align: 'center' });
    
    // Subtitle
    doc?.setFontSize(14);
    doc?.setFont('helvetica', 'italic');
    doc?.text('Proven Frameworks for Data Transformation', pageWidth / 2, 150, { align: 'center' });
    
    // Bottom text
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.text(`© ${new Date()?.getFullYear()} Northstar Analytics. All rights reserved.`, pageWidth / 2, pageHeight - 20, { align: 'center' });

    // Page 2 - Introduction
    doc?.addPage();
    yPosition = margin;
    
    doc?.setTextColor(...darkGray);
    doc?.setFontSize(20);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Introduction', margin, yPosition);
    yPosition += 15;
    
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'normal');
    const introText = [
      'At Northstar Analytics, we believe that successful data transformation requires more than just',
      'technology—it demands proven methodologies, strategic thinking, and deep organizational',
      'understanding. Our three proprietary frameworks represent decades of combined experience',
      'helping organizations unlock the full potential of their data assets.',
      '',
      'This guide introduces our core methodologies:',
    ];
    
    introText?.forEach(line => {
      doc?.text(line, margin, yPosition);
      yPosition += 6;
    });
    
    yPosition += 5;
    
    // Framework list
    const frameworks = [
      { name: 'COMPASS Framework', desc: 'Comprehensive organizational maturity assessment' },
      { name: 'ELEVATE Method', desc: 'Executive-level enhancement and value acceleration' },
      { name: 'NAVIGATE Process', desc: 'End-to-end implementation and governance' }
    ];
    
    frameworks?.forEach(fw => {
      doc?.setFillColor(...secondaryColor);
      doc?.circle(margin + 2, yPosition - 2, 2, 'F');
      doc?.setFont('helvetica', 'bold');
      doc?.text(fw?.name, margin + 8, yPosition);
      yPosition += 6;
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...lightGray);
      doc?.text(fw?.desc, margin + 8, yPosition);
      doc?.setTextColor(...darkGray);
      yPosition += 10;
    });

    // COMPASS Framework
    doc?.addPage();
    yPosition = margin;
    
    doc?.setFillColor(...primaryColor);
    doc?.rect(margin, yPosition, pageWidth - 2 * margin, 25, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(18);
    doc?.setFont('helvetica', 'bold');
    doc?.text('COMPASS Framework', margin + 5, yPosition + 10);
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'italic');
    doc?.text('Comprehensive Organizational Maturity & Performance Assessment', margin + 5, yPosition + 18);
    yPosition += 35;
    
    doc?.setTextColor(...darkGray);
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'normal');
    const compassDesc = [
      'Our proprietary methodology for evaluating and elevating data maturity across all',
      'organizational dimensions. COMPASS provides a structured approach to understanding',
      'your current state and charting a clear path to data excellence.'
    ];
    compassDesc?.forEach(line => {
      doc?.text(line, margin, yPosition);
      yPosition += 6;
    });
    yPosition += 10;

    const compassPhases = [
      { letter: 'C', title: 'Current State', desc: 'Comprehensive assessment of existing capabilities' },
      { letter: 'O', title: 'Objectives', desc: 'Strategic alignment with business goals' },
      { letter: 'M', title: 'Methodology', desc: 'Customized approach design' },
      { letter: 'P', title: 'Planning', desc: 'Detailed roadmap creation' },
      { letter: 'A', title: 'Action', desc: 'Implementation execution' },
      { letter: 'S', title: 'Sustain', desc: 'Continuous improvement practices' },
      { letter: 'S', title: 'Scale', desc: 'Organizational scaling' }
    ];

    compassPhases?.forEach((phase, index) => {
      checkPageBreak(25);
      
      // Phase box
      doc?.setFillColor(240, 245, 255);
      doc?.roundedRect(margin, yPosition, pageWidth - 2 * margin, 20, 3, 3, 'F');
      
      // Letter circle
      doc?.setFillColor(...secondaryColor);
      doc?.circle(margin + 8, yPosition + 10, 5, 'F');
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text(phase?.letter, margin + 8, yPosition + 12, { align: 'center' });
      
      // Phase title
      doc?.setTextColor(...darkGray);
      doc?.setFontSize(12);
      doc?.text(phase?.title, margin + 18, yPosition + 8);
      
      // Phase description
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...lightGray);
      doc?.text(phase?.desc, margin + 18, yPosition + 15);
      
      yPosition += 25;
    });

    // ELEVATE Method
    doc?.addPage();
    yPosition = margin;
    
    doc?.setFillColor(...accentColor);
    doc?.rect(margin, yPosition, pageWidth - 2 * margin, 25, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(18);
    doc?.setFont('helvetica', 'bold');
    doc?.text('ELEVATE Method', margin + 5, yPosition + 10);
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'italic');
    doc?.text('Executive-Level Enhancement & Value Acceleration Through Excellence', margin + 5, yPosition + 18);
    yPosition += 35;
    
    doc?.setTextColor(...darkGray);
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'normal');
    const elevateDesc = [
      'Specialized approach for developing executive-level data thinking throughout the',
      'organization. ELEVATE focuses on leadership engagement and strategic value creation.'
    ];
    elevateDesc?.forEach(line => {
      doc?.text(line, margin, yPosition);
      yPosition += 6;
    });
    yPosition += 10;

    const elevatePhases = [
      { letter: 'E', title: 'Evaluate', desc: 'Executive readiness assessment' },
      { letter: 'L', title: 'Learn', desc: 'Knowledge transfer and education' },
      { letter: 'E', title: 'Engage', desc: 'Stakeholder activation' },
      { letter: 'V', title: 'Visualize', desc: 'Future state design' },
      { letter: 'A', title: 'Activate', desc: 'Initiative launch' },
      { letter: 'T', title: 'Transform', desc: 'Organizational change' },
      { letter: 'E', title: 'Excel', desc: 'Performance excellence' }
    ];

    elevatePhases?.forEach((phase) => {
      checkPageBreak(25);
      
      doc?.setFillColor(240, 253, 244);
      doc?.roundedRect(margin, yPosition, pageWidth - 2 * margin, 20, 3, 3, 'F');
      
      doc?.setFillColor(...accentColor);
      doc?.circle(margin + 8, yPosition + 10, 5, 'F');
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text(phase?.letter, margin + 8, yPosition + 12, { align: 'center' });
      
      doc?.setTextColor(...darkGray);
      doc?.setFontSize(12);
      doc?.text(phase?.title, margin + 18, yPosition + 8);
      
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...lightGray);
      doc?.text(phase?.desc, margin + 18, yPosition + 15);
      
      yPosition += 25;
    });

    // NAVIGATE Process
    doc?.addPage();
    yPosition = margin;
    
    doc?.setFillColor(...primaryColor);
    doc?.rect(margin, yPosition, pageWidth - 2 * margin, 25, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(18);
    doc?.setFont('helvetica', 'bold');
    doc?.text('NAVIGATE Process', margin + 5, yPosition + 10);
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'italic');
    doc?.text('Value-driven Implementation & Growth Acceleration', margin + 5, yPosition + 18);
    yPosition += 35;
    
    doc?.setTextColor(...darkGray);
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'normal');
    const navigateDesc = [
      'End-to-end implementation framework ensuring successful delivery and sustainable',
      'outcomes. NAVIGATE provides structured guidance from requirements through evolution.'
    ];
    navigateDesc?.forEach(line => {
      doc?.text(line, margin, yPosition);
      yPosition += 6;
    });
    yPosition += 10;

    const navigatePhases = [
      { letter: 'N', title: 'Needs', desc: 'Requirements definition' },
      { letter: 'A', title: 'Analyze', desc: 'Solution architecture' },
      { letter: 'V', title: 'Validate', desc: 'Proof of concept' },
      { letter: 'I', title: 'Implement', desc: 'Full deployment' },
      { letter: 'G', title: 'Govern', desc: 'Oversight and control' },
      { letter: 'A', title: 'Adopt', desc: 'User adoption' },
      { letter: 'T', title: 'Track', desc: 'Performance monitoring' },
      { letter: 'E', title: 'Evolve', desc: 'Continuous evolution' }
    ];

    navigatePhases?.forEach((phase) => {
      checkPageBreak(25);
      
      doc?.setFillColor(240, 245, 255);
      doc?.roundedRect(margin, yPosition, pageWidth - 2 * margin, 20, 3, 3, 'F');
      
      doc?.setFillColor(...primaryColor);
      doc?.circle(margin + 8, yPosition + 10, 5, 'F');
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text(phase?.letter, margin + 8, yPosition + 12, { align: 'center' });
      
      doc?.setTextColor(...darkGray);
      doc?.setFontSize(12);
      doc?.text(phase?.title, margin + 18, yPosition + 8);
      
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...lightGray);
      doc?.text(phase?.desc, margin + 18, yPosition + 15);
      
      yPosition += 25;
    });

    // Key Principles Page
    doc?.addPage();
    yPosition = margin;
    
    doc?.setTextColor(...darkGray);
    doc?.setFontSize(20);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Key Principles', margin, yPosition);
    yPosition += 15;
    
    const principles = [
      { title: 'Data-Driven', desc: 'Evidence-based decision making at every stage' },
      { title: 'Proven', desc: 'Battle-tested across industries and organization sizes' },
      { title: 'Adaptable', desc: 'Customized to your unique context and constraints' },
      { title: 'Scalable', desc: 'Frameworks that grow with your organization' },
      { title: 'Sustainable', desc: 'Long-term capability building, not quick fixes' },
      { title: 'Collaborative', desc: 'Partnership approach with knowledge transfer' }
    ];

    principles?.forEach((principle) => {
      checkPageBreak(20);
      
      doc?.setFillColor(...accentColor);
      doc?.circle(margin + 3, yPosition - 1, 2, 'F');
      
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(...darkGray);
      doc?.text(principle?.title, margin + 10, yPosition);
      yPosition += 6;
      
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...lightGray);
      doc?.text(principle?.desc, margin + 10, yPosition);
      yPosition += 12;
    });

    // Contact Page
    doc?.addPage();
    yPosition = pageHeight / 2 - 40;
    
    doc?.setFillColor(...primaryColor);
    doc?.rect(0, yPosition - 10, pageWidth, 80, 'F');
    
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(22);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Ready to Transform Your Data Strategy?', pageWidth / 2, yPosition + 10, { align: 'center' });
    
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'normal');
    doc?.text('Contact us to discuss how our methodologies can accelerate', pageWidth / 2, yPosition + 25, { align: 'center' });
    doc?.text('your organization\'s data transformation journey.', pageWidth / 2, yPosition + 32, { align: 'center' });

    doc?.setFontSize(11);
    doc?.text('www.northstaranalytics.eu', pageWidth / 2, yPosition + 45, { align: 'center' });
    doc?.text('info@northstaranalytics.eu', pageWidth / 2, yPosition + 52, { align: 'center' });

    // Save the PDF
    doc?.save('Northstar-Analytics-Methodology-Guide.pdf');
  };

  const frameworks = [
    {
      id: 'compass',
      name: 'COMPASS Framework',
      tagline: 'Comprehensive Organizational Maturity & Performance Assessment',
      icon: 'Compass',
      color: 'blue',
      description: 'Our proprietary methodology for evaluating and elevating data maturity across all organizational dimensions. COMPASS provides a structured approach to understanding your current state and charting a clear path to data excellence.',
      phases: [
        {
          phase: "C - Current State",
          title: "Comprehensive Assessment",
          description: "Deep-dive analysis of existing data capabilities, infrastructure, and organizational readiness.",
          activities: ["Data landscape mapping", "Capability gap analysis", "Stakeholder interviews", "Technical architecture review"]
        },
        {
          phase: "O - Objectives",
          title: "Strategic Alignment",
          description: "Define clear, measurable objectives that align data initiatives with business strategy.",
          activities: ["Business outcome definition", "Success metrics establishment", "ROI framework creation", "Timeline development"]
        },
        {
          phase: "M - Methodology",
          title: "Approach Design",
          description: "Customize implementation methodology based on organizational context and constraints.",
          activities: ["Framework selection", "Change management planning", "Risk mitigation strategies", "Resource allocation"]
        },
        {
          phase: "P - Planning",
          title: "Detailed Roadmap",
          description: "Create comprehensive implementation roadmap with clear milestones and dependencies.",
          activities: ["Project planning", "Resource scheduling", "Dependency mapping", "Milestone definition"]
        },
        {
          phase: "A - Action",
          title: "Implementation",
          description: "Execute planned initiatives with continuous monitoring and adjustment.",
          activities: ["Solution deployment", "Team training", "Process implementation", "Quality assurance"]
        },
        {
          phase: "S - Sustain",
          title: "Continuous Improvement",
          description: "Establish sustainable practices for ongoing data maturity advancement.",
          activities: ["Performance monitoring", "Continuous training", "Process optimization", "Capability expansion"]
        },
        {
          phase: "S - Scale",
          title: "Organizational Scaling",
          description: "Expand successful practices across the entire organization.",
          activities: ["Best practice sharing", "Cross-team collaboration", "Enterprise integration", "Culture embedding"]
        }
      ]
    },
    {
      id: 'elevate',
      name: 'ELEVATE Method',
      tagline: 'Executive-Level Enhancement & Value Acceleration Through Excellence',
      icon: 'TrendingUp',
      color: 'primary',
      description: 'Specialized approach for developing executive-level data thinking throughout the organization.',
      phases: [
        {
          phase: "E - Evaluate",
          title: "Executive Readiness",
          description: "Assess leadership commitment and organizational readiness for strategic data transformation.",
          activities: ["Leadership assessment", "Cultural evaluation", "Change readiness analysis", "Executive alignment"]
        },
        {
          phase: "L - Learn",
          title: "Knowledge Transfer",
          description: "Develop deep understanding of data strategy principles and best practices.",
          activities: ["Executive education", "Framework training", "Case study analysis", "Peer learning sessions"]
        },
        {
          phase: "E - Engage",
          title: "Stakeholder Activation",
          description: "Build cross-functional engagement and commitment to data transformation.",
          activities: ["Stakeholder workshops", "Communication planning", "Resistance management", "Champion development"]
        },
        {
          phase: "V - Visualize",
          title: "Future State Design",
          description: "Create compelling vision of data-driven organizational future.",
          activities: ["Vision development", "Success visualization", "Benefit quantification", "Roadmap creation"]
        },
        {
          phase: "A - Activate",
          title: "Initiative Launch",
          description: "Launch strategic data initiatives with proper governance and oversight.",
          activities: ["Project initiation", "Governance establishment", "Team formation", "Resource allocation"]
        },
        {
          phase: "T - Transform",
          title: "Organizational Change",
          description: "Execute transformation with focus on sustainable capability building.",
          activities: ["Process transformation", "System implementation", "Skill development", "Culture change"]
        },
        {
          phase: "E - Excel",
          title: "Performance Excellence",
          description: "Achieve and maintain high-performance data capabilities.",
          activities: ["Performance optimization", "Continuous improvement", "Innovation fostering", "Excellence maintenance"]
        }
      ]
    },
    {
      id: 'navigate',
      name: 'NAVIGATE Process',
      tagline: 'Northstar Analytics Value-driven Implementation & Growth Acceleration Through Excellence',
      icon: 'Navigation',
      color: 'accent',
      description: 'End-to-end process for implementing and scaling data solutions with measurable business impact.',
      phases: [
        {
          phase: "N - Needs",
          title: "Requirements Analysis",
          description: "Comprehensive understanding of business needs and technical requirements.",
          activities: ["Business requirement gathering", "Technical needs assessment", "Constraint identification", "Priority establishment"]
        },
        {
          phase: "A - Analyze",
          title: "Solution Architecture",
          description: "Design optimal solution architecture considering all organizational factors.",
          activities: ["Architecture design", "Technology selection", "Integration planning", "Scalability assessment"]
        },
        {
          phase: "V - Validate",
          title: "Proof of Concept",
          description: "Validate proposed solutions through controlled testing and stakeholder feedback.",
          activities: ["Prototype development", "Stakeholder validation", "Performance testing", "Risk assessment"]
        },
        {
          phase: "I - Implement",
          title: "Solution Deployment",
          description: "Deploy validated solutions with comprehensive testing and quality assurance.",
          activities: ["System deployment", "Data migration", "Integration testing", "User acceptance testing"]
        },
        {
          phase: "G - Govern",
          title: "Governance Implementation",
          description: "Establish robust governance frameworks for sustainable data management.",
          activities: ["Policy development", "Process establishment", "Role definition", "Compliance framework"]
        },
        {
          phase: "A - Adopt",
          title: "User Adoption",
          description: "Ensure successful user adoption through training and change management.",
          activities: ["User training", "Change management", "Support system setup", "Adoption monitoring"]
        },
        {
          phase: "T - Track",
          title: "Performance Monitoring",
          description: "Continuous monitoring and optimization of solution performance.",
          activities: ["Performance monitoring", "KPI tracking", "Issue resolution", "Optimization planning"]
        },
        {
          phase: "E - Evolve",
          title: "Continuous Evolution",
          description: "Evolve solutions to meet changing business needs and technological advances.",
          activities: ["Capability enhancement", "Technology updates", "Process refinement", "Innovation integration"]
        }
      ]
    }
  ];

  const currentFramework = frameworks?.[activeFramework];

  return (
    <section id="proven-methodologies" className="py-12 sm:py-16 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="flex items-center justify-center space-x-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent/10 rounded-full flex items-center justify-center">
              <Icon name="Settings" size={20} className="text-accent sm:w-6 sm:h-6" />
            </div>
            <span className="text-accent font-semibold text-base sm:text-lg">Proven Methodologies</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary mb-4 sm:mb-6 px-2">
            Proprietary Frameworks That Deliver Results
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto px-2">
            Our battle-tested methodologies combine strategic thinking with practical implementation, ensuring sustainable transformation and measurable business impact.
          </p>
        </div>

        {/* Framework Selector */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {frameworks?.map((framework, index) => (
            <button
              key={index}
              onClick={() => setActiveFramework(index)}
              className={`p-6 rounded-xl text-left transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-95 group ${
                activeFramework === index
                  ? 'bg-gradient-to-br from-primary to-secondary text-white shadow-lg scale-105'
                  : 'bg-white hover:bg-surface border-2 border-border hover:border-secondary'
              }`}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  activeFramework === index ? 'bg-white/20' : 'bg-primary/10'
                }`}>
                  <Icon 
                    name={framework?.icon} 
                    size={24} 
                    className={`transition-transform duration-300 group-hover:rotate-12 ${
                      activeFramework === index ? 'text-white' : 'text-primary'
                    }`} 
                  />
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${
                    activeFramework === index ? 'text-white' : 'text-primary'
                  }`}>
                    {framework?.name}
                  </h3>
                </div>
              </div>
              <p className={`text-sm ${
                activeFramework === index ? 'text-white/90' : 'text-muted-foreground'
              }`}>
                {framework?.tagline}
              </p>
            </button>
          ))}
        </div>

        {/* Framework Details */}
        <div className="lg:w-2/3">
          <div className="bg-surface rounded-2xl p-4 sm:p-6 lg:p-8">
            {/* Framework Header */}
            <div className="flex items-center space-x-3 sm:space-x-4 mb-6 sm:mb-8">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                currentFramework?.color === 'secondary' ? 'bg-secondary/10' :
                currentFramework?.color === 'primary' ? 'bg-primary/10' : 'bg-accent/10'
              }`}>
                <Icon 
                  name={currentFramework?.icon} 
                  size={24}
                  className={`sm:w-7 sm:h-7 lg:w-8 lg:h-8 ${
                    currentFramework?.color === 'secondary' ? 'text-secondary' :
                    currentFramework?.color === 'primary' ? 'text-primary' : 'text-accent'
                  }`} 
                />
              </div>
              <div className="min-w-0">
                <h3 className="text-xl sm:text-2xl font-bold text-primary truncate">{currentFramework?.name}</h3>
                <p className="text-sm sm:text-base text-muted-foreground line-clamp-2">{currentFramework?.subtitle}</p>
              </div>
            </div>

            {/* Framework Phases */}
            <div className="space-y-6">
              {currentFramework?.phases?.map((phase, index) => (
                <div key={index} className="bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:scale-102 group">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                      <span className="text-white font-bold text-xl">{phase?.number}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2 sm:mb-3">
                        <h4 className="text-base sm:text-lg font-bold text-primary">{phase?.title}</h4>
                        <span className="text-xs sm:text-sm font-medium text-muted-foreground">({phase?.phase})</span>
                      </div>
                      <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">{phase?.description}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {phase?.activities?.map((activity, actIndex) => (
                          <div key={actIndex} className="flex items-start space-x-2">
                            <Icon name="Check" size={14} className="text-secondary flex-shrink-0 mt-0.5" />
                            <span className="text-xs sm:text-sm text-muted-foreground">{activity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Methodology Benefits */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-6 sm:p-8 text-white">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Why Our Methodologies Work</h3>
            <p className="text-sm sm:text-base text-white/90 max-w-3xl mx-auto">
              Our frameworks are built from 30+ years of real-world implementation experience, refined through successful transformations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Icon name="Target" size={28} className="text-white sm:w-8 sm:h-8" />
              </div>
              <h4 className="text-base sm:text-lg font-bold mb-2">Proven Results</h4>
              <p className="text-white/80 text-xs sm:text-sm">Measurable business outcomes</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Icon name="Users" size={28} className="text-white sm:w-8 sm:h-8" />
              </div>
              <h4 className="text-base sm:text-lg font-bold mb-2">Human-Centered</h4>
              <p className="text-white/80 text-xs sm:text-sm">Focus on people and culture, not just technology</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Icon name="Repeat" size={28} className="text-white sm:w-8 sm:h-8" />
              </div>
              <h4 className="text-base sm:text-lg font-bold mb-2">Scalable</h4>
              <p className="text-white/80 text-xs sm:text-sm">Frameworks that grow with your organization</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleDownloadGuide}
              variant="primary"
              className="transition-all duration-200 hover:scale-105 hover:shadow-lg focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-95"
            >
              <Icon name="Download" size={20} className="transition-transform duration-300 group-hover:translate-y-1" />
              Download Full Methodology Guide
            </Button>
            <Button
              onClick={() => window.location.href = '/contact-multi-channel-engagement-hub'}
              variant="outline"
              className="transition-all duration-200 hover:scale-105 hover:shadow-lg focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MethodologySection;